import { Accordion, Box, Icon, Span } from '@chakra-ui/react';
import type React from 'react';
import { useRef, useState } from 'react';
import { BiPlus } from 'react-icons/bi';
import { IoTrashOutline } from 'react-icons/io5';
import type {
	CustomAccordionItemBodyProps,
	CustomAccordionItemContentProps,
	CustomAccordionItemIndicatorProps,
	CustomAccordionItemProps,
	CustomAccordionItemTriggerProps,
	CustomAccordionRootProps,
} from '@/components/Atoms/FaceliftAccordion/FaceliftAccordion.types';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import Wound1 from '@/components/Templates/BenefitVerification/Steps/Wound1';
import Wound2 from '@/components/Templates/BenefitVerification/Steps/Wound2';
import Wound3 from '@/components/Templates/BenefitVerification/Steps/Wound3';
import type { FormMethods } from '@/types/form';

const CustomAccordion = {
	Root: (props: CustomAccordionRootProps) => <Accordion.Root {...props} />,
	Item: (props: CustomAccordionItemProps) => (
		<Accordion.Item border="none" {...props} />
	),
	ItemTrigger: (props: CustomAccordionItemTriggerProps) => (
		<Accordion.ItemTrigger {...props} />
	),
	ItemIndicator: (props: CustomAccordionItemIndicatorProps) => (
		<Accordion.ItemIndicator {...props} />
	),
	ItemContent: (props: CustomAccordionItemContentProps) => (
		<Accordion.ItemContent {...props} />
	),
	ItemBody: (props: CustomAccordionItemBodyProps) => (
		<Accordion.ItemBody {...props} />
	),
};

export interface WoundData {
	id: string;
	procedureDate: string;
	applicationCode1: string;
	applicationCode2: string;
	woundLength: string;
	woundWidth: string;
	woundDepth: string;
	primaryDiagnosis: string;
	diagnosisCode2: string;
	diagnosisCode3: string;
}

export interface WoundInformationData {
	wounds: WoundData[];
}

export interface WoundInformationProps {
	formMethods: FormMethods<BVFormData>;
}

const WoundInformation: React.FC<WoundInformationProps> = ({ formMethods }) => {
	const { getValues, watch, setValue } = formMethods;
	const [openAccordionValues, setOpenAccordionValues] = useState<string[]>(['Wound-2', 'Wound-3']);
	const scrollableBoxRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		setTimeout(() => {
			if (scrollableBoxRef.current) {
				scrollableBoxRef.current.scrollTo({
					top: 600 * (watch('totalWoundsCreated') - 1),
					behavior: 'smooth',
				});
			}
		}, 100);
	};

	return (
		<Box>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				color={{ base: 'gray.800', _dark: 'gray.100' }}
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Wound Information
			</FaceliftText>
			<Box ref={scrollableBoxRef} overflow="auto" minH={600} maxH={600}>
				<FaceliftText
					fontSize={{ base: 'sm', md: 'md' }}
					fontWeight="semibold"
					color={{ base: 'gray.800', _dark: 'gray.100' }}
				>
					Wound 1
				</FaceliftText>
				<Wound1 formMethods={formMethods} woundNumber={0} />

				<CustomAccordion.Root
					multiple
					collapsible
					defaultValue={['Wound-2', 'Wound-3']}
					value={openAccordionValues}
					onValueChange={(details: { value: string[] }) => setOpenAccordionValues(details.value)}
				>
					<Show when={watch('totalWoundsCreated') > 1}>
						<CustomAccordion.Item value={'Wound-2'}>
							<CustomAccordion.ItemTrigger>
								<Span flex="1" fontWeight={'bold'} display="flex" alignItems="center">
									{'Wound 2'}{' '}
									<Show when={watch('totalWoundsCreated') > 1}>
										<Icon
											size={'md'}
											ml={10}
											cursor={'pointer'}
											_hover={{ color: 'brand.errors' }}
											onClick={() => {
												const currentWounds = getValues('totalWoundsCreated');
												if (currentWounds > 1) {
													formMethods.setValue(
														'totalWoundsCreated',
														currentWounds - 1,
													);
													setValue('ta_Wound2_Application1@odatabind', null);
													setValue('ta_Wound2_Application2@odatabind', null);
													setValue('ta_Wound2_Diagnosis1@odatabind', null);
													setValue('ta_Wound2_Diagnosis2@odatabind', null);
													setValue('ta_Wound2_Diagnosis3@odatabind', null);
													setValue('ta_Wound2_Diagnosis4@odatabind', null);
													setValue('ta_wound2_woundlength', null);
													setValue('ta_wound2_woundwidth', null);
													setValue('ta_wound2_wounddepth', null);
													setValue('ta_wound2_woundsizetotal', null);
													setValue('ta_woundtype2', null);
													// Remove from open accordion values
													setOpenAccordionValues((prev) => prev.filter((val) => val !== 'Wound-2'));
												}
											}}
										>
											<IoTrashOutline />
										</Icon>
									</Show>
								</Span>
								<Accordion.ItemIndicator />
							</CustomAccordion.ItemTrigger>
							<CustomAccordion.ItemContent>
								<Accordion.ItemBody>
									<Wound2 formMethods={formMethods} woundNumber={1} />
								</Accordion.ItemBody>
							</CustomAccordion.ItemContent>
						</CustomAccordion.Item>
					</Show>

					<Show when={watch('totalWoundsCreated') > 2}>
						<CustomAccordion.Item value={'Wound-3'}>
							<CustomAccordion.ItemTrigger>
								<Span flex="1" fontWeight={'bold'} display="flex" alignItems="center">
									{'Wound 3'}
									<Show when={watch('totalWoundsCreated') > 2}>
										<Icon
											size={'md'}
											ml={10}
											cursor={'pointer'}
											_hover={{ color: 'brand.errors' }}
											onClick={() => {
												const currentWounds = getValues('totalWoundsCreated');
												if (currentWounds > 2) {
													formMethods.setValue(
														'totalWoundsCreated',
														currentWounds - 1,
													);
													setValue('ta_Wound3_Application1@odatabind', null);
													setValue('ta_Wound3_Application2@odatabind', null);
													setValue('ta_Wound3_Diagnosis1@odatabind', null);
													setValue('ta_Wound3_Diagnosis2@odatabind', null);
													setValue('ta_Wound3_Diagnosis3@odatabind', null);
													setValue('ta_Wound3_Diagnosis4@odatabind', null);
													setValue('ta_wound3_woundlength', null);
													setValue('ta_wound3_woundwidth', null);
													setValue('ta_wound3_wounddepth', null);
													setValue('ta_wound3_woundsizetotal', null);
													setValue('ta_woundtype3', null);
													// Remove from open accordion values
													setOpenAccordionValues((prev) => prev.filter((val) => val !== 'Wound-3'));
												}
											}}
										>
											<IoTrashOutline />
										</Icon>
									</Show>
								</Span>
								<Accordion.ItemIndicator />
							</CustomAccordion.ItemTrigger>
							<CustomAccordion.ItemContent>
								<Accordion.ItemBody>
									<Wound3 formMethods={formMethods} woundNumber={2} />
								</Accordion.ItemBody>
							</CustomAccordion.ItemContent>
						</CustomAccordion.Item>
					</Show>
				</CustomAccordion.Root>
			</Box>
			<FaceliftButton
				bg={'transparent'}
				color="brand.solid"
				fontWeight="bold"
				border={'1px solid'}
				_hover={{ color: 'brand.solidSecondary', cursor: 'pointer' }}
				disabled={watch('totalWoundsCreated') === 3}
				onClick={() => {
					const currentWounds = getValues('totalWoundsCreated');
					if (currentWounds < 3) {
						const newWoundCount = currentWounds + 1;
						formMethods.setValue('totalWoundsCreated', newWoundCount);
						// Open the newly added wound accordion (add to array without removing existing)
						if (newWoundCount === 2) {
							setOpenAccordionValues((prev) =>
								prev.includes('Wound-2') ? prev : [...prev, 'Wound-2']
							);
						} else if (newWoundCount === 3) {
							setOpenAccordionValues((prev) =>
								prev.includes('Wound-3') ? prev : [...prev, 'Wound-3']
							);
						}
						// Scroll to bottom after adding wound
						scrollToBottom();
					}
				}}
				my={2}
			>
				Add Wound{' '}
				<Icon>
					<BiPlus />
				</Icon>
			</FaceliftButton>
		</Box>
	);
};

export default WoundInformation;
